import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { BehaviorSubject } from 'rxjs';
import { InfoCategory } from '../../shared/models/app/info-category';
import {
  CategoriesEnum,
  SubCategoriesEnum,
} from '../../shared/models/app/info-enum';
import { Product } from '../../shared/models/product/product';
import { AppInfoService } from '../app-info/app-info.service';

export class MetaTagsData {
  public description: string = '';
  public h1: string = '';
  public h2: string = '';
  public title: string = '';
  public keywords: string = '';
  public urlImage: string = '';
}

@Injectable({
  providedIn: 'root',
})
export class MetaTagsService {
  public static readonly PAGE_ABOUT = 'page-about';
  public static readonly PAGE_BUDGET = 'page-budget';
  public static readonly PAGE_CART = 'page-cart';
  public static readonly PAGE_CATALOG = 'page-catalog';
  public static readonly PAGE_CATEGORIES = 'page-categories';
  public static readonly PAGE_CONTACT = 'page-contact';
  public static readonly PAGE_CUSTOMER_AREA = 'page-customer-area';
  public static readonly PAGE_CUSTOMER_AREA_ADDRESS =
    'page-customer-area-address';
  public static readonly PAGE_CUSTOMER_AREA_DATA = 'page-customer-area-data';
  public static readonly PAGE_CUSTOMER_AREA_ORDERS =
    'page-customer-area-orders';
  public static readonly PAGE_DEALER = 'page-dealer';
  public static readonly PAGE_FAQ = 'page-faq';
  public static readonly PAGE_FORGOT = 'page-forgot';
  public static readonly PAGE_HOME = 'page-home';
  public static readonly PAGE_LOGIN = 'page-login';
  public static readonly PAGE_NOT_FOUND = 'page-not-found';
  public static readonly PAGE_ORDER_PLACED = 'page-order-placed';
  public static readonly PAGE_PAYMENT = 'page-payment';
  public static readonly PAGE_POLICIES = 'page-policies';
  public static readonly PAGE_PRIVACY = 'page-privacy';
  public static readonly PAGE_PRODUCTS = 'page-products';
  public static readonly PAGE_REGISTER = 'page-register';
  public static readonly PAGE_SAC = 'page-sac';
  public static readonly PAGE_SUB_CATEGORIES = 'page-sub-categories';
  public static readonly PAGE_TERMS = 'page-terms';

  public static readonly TAG_DESCRIPTION = 'description';
  public static readonly TAG_KEYWORDS = 'keywords';
  public static readonly TAG_OG_DESCRIPTION = 'og:description';
  public static readonly TAG_OG_IMAGE = 'og:image';

  public static readonly KEYWORDS_DEFAULT =
    'Encontre chapa de policarbonato, kits e acessórios para coberturas e toldos. Qualidade, resistência e ótimo custo-benefício direto de fábrica!';

  private h1Subject = new BehaviorSubject<string>('');
  private _h1: string = 'RM Policarbonatos e Acessórios';
  public h1$ = this.h1Subject.asObservable();

  public _categories: Array<InfoCategory> = [];
  private renderer: Renderer2;

  constructor(
    private appInfoService: AppInfoService,
    private meta: Meta,
    public title: Title,
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private dom: any
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  setH1(value: string) {
    this._h1 = value;
    this.h1Subject.next(value);
  }

  get h1(): string {
    return this._h1;
  }

  addTag(tag: MetaDefinition) {
    this.meta.addTag(tag);
  }

  addTags(tags: MetaDefinition[]) {
    this.meta.addTags(tags);
  }

  getTag(tags: string) {
    return this.meta.getTag(tags);
  }

  updateTagByName(keyTag: string, value: string) {
    const metaTag = this.getTag(`name='${keyTag}'`);
    if (metaTag) {
      metaTag.content = value;
    } else {
      this.addTag({ name: keyTag, content: value });
    }
  }

  updateTagByProperty(keyTag: string, value: string) {
    const metaTag = this.getTag(`property='${keyTag}'`);
    if (metaTag) {
      metaTag.content = value;
    } else {
      this.addTag({ name: keyTag, content: value });
    }
  }

  removeTag(tag: string) {
    const tagDOM = this.meta.getTag(tag);
    if (tagDOM) {
      this.meta.removeTagElement(tagDOM);
    }
    this.meta.removeTag(tag);
  }

  setTitle(title: string) {
    this.title.setTitle(title);
  }

  updateCanonicalUrl(urlPath: string, category: string, subCategory: string) {
    if (urlPath.charAt(0) === '/') {
      urlPath = urlPath.replace(/\//g, '');
    }
    let url = '';
    if (subCategory) {
      url = subCategory + '/' + urlPath;
    } else if (category) {
      url = category + '/' + urlPath;
    } else {
      url = urlPath;
    }

    const head = this.dom.getElementsByTagName('head')[0];
    var element: HTMLLinkElement =
      this.dom.querySelector(`link[rel='canonical']`) || null;
    if (element == null) {
      element = this.dom.createElement('link') as HTMLLinkElement;
      head.appendChild(element);
    }
    element.setAttribute('rel', 'canonical');
    element.setAttribute('href', 'https://www.rmpolicarbonatos.com.br/' + url);
  }

  configTagPages(
    page: string,
    path: string,
    category: string,
    subCategory: string,
    product: Product | string | null
  ): MetaTagsData {
    const metaData: MetaTagsData = this.getMetaTagsData(
      page,
      path,
      category,
      subCategory,
      product
    );
    this.setTitle(metaData?.title.substring(0, 60));
    this.setH1(metaData?.h1);
    this.updateCanonicalUrl(path, category, subCategory);
    this.updateTagByName(MetaTagsService.TAG_DESCRIPTION, metaData.description);
    this.updateTagByName(MetaTagsService.TAG_KEYWORDS, metaData.keywords);
    this.updateTagByProperty(
      MetaTagsService.TAG_OG_DESCRIPTION,
      metaData?.description?.substring(0, 320)
    );
    this.updateTagByProperty(MetaTagsService.TAG_OG_IMAGE, metaData.urlImage);
    return metaData;
  }

  getMetaTagsData(
    page: string,
    path: string,
    category: string,
    subCategory: string,
    product: Product | string | null
  ): MetaTagsData {
    const home: MetaTagsData = {
      description:
        'Encontre chapa de policarbonato, kits e acessórios para coberturas e toldos. Qualidade, resistência e ótimo custo-benefício direto de fábrica!',
      h1: 'RM Policarbonatos - Chapas, Kits e Acessórios em SP',
      h2: '',
      title: 'RM Policarbonatos - Chapas, Kits e Acessórios em SC',
      urlImage:
        'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
      keywords: MetaTagsService.KEYWORDS_DEFAULT,
    };

    switch (page) {
      case MetaTagsService.PAGE_ABOUT:
        return {
          description:
            'A RM Policarbonatos é uma empresa Brasileira, que atua com venda online e presencial e acredita que a venda de materiais pode ser fácil, descomplicada e segura. Presta suporte aos clientes desde o pedido até a instalação.',
          h1: 'Quem somos',
          h2: '',
          title: 'Conheça a empresa - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_CART:
        return {
          description:
            'Confirma os itens do seu carrinho e prossiga para o checkout para concluir sua compra agora mesmo na RM Policarbonatos e Acessórios.',
          h1: 'Carrinho de compras',
          h2: '',
          title: 'Carrinho de compras - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_BUDGET:
        return {
          description:
            'Confirma os itens do seu orçamento e prossiga para o checkout para concluir sua compra agora mesmo na RM Policarbonatos e Acessórios.',
          h1: 'Orçamento Personalizado',
          h2: '',
          title: 'Orçamento Personalizado - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_CATALOG:
        return {
          description:
            'Baixe os catálogos em PDF dos nossos e saiba o passo a passo de instalação e garantia. Tenha acesso às informações detalhadas dos produtos e confie na qualidade da RM Policarbonatos e acessórios.',
          h1: 'Catálogos e garantias',
          h2: '',
          title: 'Catálogos e Garantias - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_CATEGORIES:
        return this.getMetaTagPageCategory(path);

      case MetaTagsService.PAGE_FORGOT:
        return {
          description:
            'Redefina uma nova senha para conntinuar tendo acesso à todas as nossa promoções ',
          h1: 'Redefinir senha',
          h2: '',
          title: 'Redefinir Senha - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_ORDER_PLACED:
        return {
          description:
            'Pedido Realizado. Aguardando confirmação de pagamento! ',
          h1: 'Pedido Realizado',
          h2: '',
          title: 'Pedido Realizado - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_CONTACT:
        return {
          description:
            'Entre em contato conosco através do formulário de contato ou whatsapp 48 3369-7157. Tire suas dúvidas, solicite suporte. Nosso time de vendas está pronto para te ajudar aqui na RM Policarbonatos e Acessórios. ',
          h1: 'Fale Conosco',
          h2: '',
          title: 'Entre em contato com a loja - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_SAC:
        return {
          description:
            'Entre em contato com o SAC através do formulário de contato. Tire suas dúvidas, solicite suporte. Nosso time de vendas está pronto para te ajudar aqui na RM Policarbonatos e Acessórios. ',
          h1: 'SAC - Serviço de Atendimento ao Cliente',
          h2: '',
          title: 'Entre em contato com o SAC - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_FAQ:
        return {
          description:
            'Lista das perguntas mais frequentes. Tire suas dúvidas, e se não encontrar o que procura, solicite suporte. Nosso time de suporte está pronto para te ajudar aqui na RM Policarbonatos e Acessórios. ',
          h1: 'FAQ - Perguntas Frequentes',
          h2: '',
          title: 'Perguntas Frequentes - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_DEALER:
        return {
          description:
            'Entre em contato através do formulário para se tornar um revendedor da RM Policarbonatos e Acessórios. ',
          h1: 'Seja um Revendedor',
          h2: '',
          title: 'Seja um Revendedor - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_CUSTOMER_AREA:
        return {
          description:
            'Acesse seus pedidos, seus dados de cadastro e seu endereço.',
          h1: 'Área do Cliente',
          h2: '',
          title: 'Área do Cliente - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_CUSTOMER_AREA_ADDRESS:
        return {
          description: 'Confira e altere seu endereço.',
          h1: 'Meu Endereço',
          h2: '',
          title: 'Meu Endereço - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_CUSTOMER_AREA_DATA:
        return {
          description: 'Confira e altere todos os seus dados pessoais.',
          h1: 'Meus Dados Pessoais',
          h2: '',
          title: 'Meus Dados Pessoais - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_CUSTOMER_AREA_ORDERS:
        return {
          description: 'Confira todos os dados sobre todos os seus pedidos.',
          h1: 'Meus Pedidos',
          h2: '',
          title: 'Meus Pedidos - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_HOME:
        return home;

      case MetaTagsService.PAGE_LOGIN:
        return {
          description: 'Acesse seu painel para ter acesso aos seus pedidos.',
          h1: 'Faça seu login',
          h2: '',
          title: 'Login - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_REGISTER:
        return {
          description:
            'Preenca todos os campos corretamente e tenhhha acesso aos nossso sprodutos e promoções.',
          h1: 'Realize seu Cadastro',
          h2: '',
          title: 'Novo Cadastro - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_NOT_FOUND:
        return {
          description:
            'A página que você tentou acessar não está disponível em nosso sistema.',
          h1: 'Página não encontrada',
          h2: '',
          title: 'Página não encontrada - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_PAYMENT:
        return {
          description:
            'Prossiga para o checkout seguro em nosso e-commerce. Insira suas informações de pagamento e endereço de entrega com tranquilidade, sabendo que seus dados estão protegidos. Garantimos uma experiência de compra segura e confiável.',
          h1: 'Checkout',
          h2: '',
          title: 'Chekout Seguro - RM Policarbonatos e Acessórios',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_POLICIES:
        return {
          description:
            'Conheça as políticas de troca e cancelamento da RM Policarbonatos e Acessórios e faça sua compra com segurança. Estamos comprometidos em garantir a sua satisfação e oferecer uma experiência de troca tranquila e transparente.',
          h1: 'Políticas de troca e cancelamento',
          h2: '',
          title: 'Política de troca, cancelamento - RM Policarbonatos',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_PRIVACY:
        return {
          description:
            'Leia nossa política de privacidade para entender como coletamos, usamos e protegemos seus dados pessoais durante sua visita ao nosso site. Respeitamos sua privacidade e nos comprometemos a manter suas informações seguras e confidenciais. ',
          h1: 'Aviso de privacidade',
          h2: '',
          title: 'Aviso de privacidade - RM Policarbonatos',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };

      case MetaTagsService.PAGE_PRODUCTS:
        return this.getMetaTagPageProducts(product);

      case MetaTagsService.PAGE_SUB_CATEGORIES:
        return this.getMetaTagPageSubCategory(category, path);

      case MetaTagsService.PAGE_TERMS:
        return {
          description:
            'Conheça os termos e condições de uso a RM Policarbonatos e Acessórios e faça sua compra com segurança. Ao continuar navegando, você concorda com nossos termos e condições.',
          h1: 'Termos de Condições e Uso',
          h2: '',
          title: 'Termos e condições de uso - RM Policarbonatos',
          urlImage:
            'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg',
          keywords: MetaTagsService.KEYWORDS_DEFAULT,
        };
      default:
        return home;
    }
  }

  getMetaTagPageCategory(path: string): MetaTagsData {
    path = path.startsWith('/') ? path.slice(1) : path;
    const meta: MetaTagsData = new MetaTagsData();
    const formatPath = path?.toUpperCase()?.replaceAll('-', '_');
    if (formatPath === CategoriesEnum.PRODUCTS_ON_OFFER_PT) {
      meta.description =
        'Confira todos os nossos produtos Black Friday e aproveite nossas promoções!';
      meta.h1 = 'Black Friday';
      meta.title = 'Black Friday - RM Policarbonatos e Acessórios';
    } else {
      meta.description = this.getDescriptionPageCategory(path);
      meta.h1 = this.getH1PageCategory(path);
      meta.title =
        this.getTitlePageCategory(path) + ' - RM Policarbonatos e Acessórios';
      meta.urlImage =
        'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg';
    }
    return meta;
  }

  getMetaTagPageSubCategory(category: string, path: string): MetaTagsData {
    const meta: MetaTagsData = new MetaTagsData();
    meta.description = this.getDescriptionPageSubCategory(category, path);
    meta.h1 = this.getH1PageSubCategory(category, path);
    meta.title = this.getTitlePageSubCategory(category, path);
    meta.urlImage =
      'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg';
    meta.keywords = this.getKeywordsPageSubCategory(path);
    return meta;
  }

  getMetaTagPageProducts(product: Product | string | null): MetaTagsData {
    const meta: MetaTagsData = new MetaTagsData();
    let title = '';

    if (product instanceof Product) {
      title = product?.name || '';
      meta.urlImage =
        product?.coverPhoto ||
        'https://d20p8exwoll05u.cloudfront.net/assets/images/logo/logo-icon.jpg';
      const session = Product.getSessionByTitle(product as Product, 'Sobre');
      let description = (session?.description || '').replace(
        /(<([^>]+)>)/gi,
        ''
      );
      description = description
        ?.replaceAll('&nbsp;', ' ')
        ?.replaceAll('&nbsp', ' ');
      meta.description = description?.substring(0, 160) + 'SKU: ' + product?.id;
    } else {
      title = ('Buscar Produtos: ' + product) as string;
      meta.description =
        'Policarbonatos em promoção! Escolha entre diversos modelos e marcas. Alveolar, compacto e muito mais. Escolha a sua com preço baixo e entrega garantida!';
    }

    const sku = (product as Product)?.id || '';
    meta.h1 = title + ' - RM Policarbonatos';
    meta.title = title?.substring(0, 40) + ' - RM Policarbonatos - ' + sku;
    return meta;
  }

  getDescriptionPageCategory(path: string) {
    return 'Descrição da Categoria: ' + this.getTitlePageCategory(path);
  }

  getH1PageCategory(path: string) {
    return 'H1 da Categoria: ' + this.getTitlePageCategory(path);
  }

  getH1PageSubCategory(category: string, subCategory: string) {
    return this.getTitlePageSubCategory(category, subCategory);
  }

  getTitlePageCategory(path: string) {
    path = path.startsWith('/') ? path.slice(1) : path;
    path = path?.toUpperCase()?.replaceAll('-', '_');
    if (path === CategoriesEnum.PRODUCTS_ON_OFFER_PT) {
      return 'Black Friday';
    } else {
      const category = this.getCategories().find((c) => c.id === path);
      if (category) {
        return category.description || ' RM Policarbonatos e Acessórios';
      }
    }
    return '';
  }

  getTitlePageSubCategory(category: string, subCategory: string) {
    category = category?.toUpperCase()?.replaceAll('-', '_');
    subCategory = subCategory?.toUpperCase()?.replaceAll('-', '_');

    const categoryInfo = this.getCategories().find((c) => c.id === category);
    if (categoryInfo) {
      const subCategoryInfo = categoryInfo?.subCategories?.find(
        (s) => s.id === subCategory
      );
      if (subCategoryInfo) {
        switch (subCategory) {
          case SubCategoriesEnum.POLICARBONATO_ALVEOLAR:
            return 'Chapa de Policarbonato Alveolar Cristal Direto da Fábrica';
          case SubCategoriesEnum.TOLDO_EM_POLICARBONATO:
            return 'Toldo em Policarbonato Alveolar Pronto para Instalar';
          case SubCategoriesEnum.POLICARBONATO_COMPACTO_KIT:
            return 'Chapa de Policarbonato Compacto Cristal e Fumê em Kit';
          case SubCategoriesEnum.ROMANA:
            return 'Persiana Romana Blackout para Quarto com Design Sofisticado';
          case SubCategoriesEnum.DOUBLE_VISION:
            return 'Persiana Double Vision Blackout para conforto e estilo';
          case SubCategoriesEnum.POLICARBONATO_COMPACTO:
            return 'Policarbonato Compacto resistente e transparente premium';
          case SubCategoriesEnum.ROLO:
            return 'Persianas Rolo com qualidade e durabilidade';
          case SubCategoriesEnum.TOLDOS_CORTINA:
            return 'Toldo cortina prático para proteção e charme externo';
          case SubCategoriesEnum.ACESSORIOS_PERSIANAS:
            return 'Persianas e Cortinas Persiana em até 4x sem Juros';
          case SubCategoriesEnum.PERFIS_FIXACAO_ACABAMENTO:
            return 'Perfis em Alumínio para Policarbonato em até 4x s/ Juros';
          case SubCategoriesEnum.POLICARBONATO_ALVEOLAR_KIT:
            return 'Kit Policarbonato Alveolar para Cobertura | RM Policarbonatos';
          case SubCategoriesEnum.TOLDO_FIXO:
            return 'Toldo Fixo em Policarbonato Sob Medida | RM Policarbonatos';
          case SubCategoriesEnum.HORIZONTAL:
            return 'Persiana Horizontal Alumínio Sob Medida Alta Qualidade';
          case SubCategoriesEnum.PRONTA_ENTREGA:
            return 'Persianas em Rolo e Double Vision | RM Policarbonatos';
          case SubCategoriesEnum.PLASTICOS_EMBALAGENS:
            return 'Embalagens de Plástico Resistente Fabrica Bobina Bolha';
          case SubCategoriesEnum.CANTONEIRAS_BARRA_CHATA:
            return 'Barra Chata Alumínio Natural Perfis Cantoneira';
          case SubCategoriesEnum.PLACAS_SINALIZACAO:
            return 'Placas de Sinalização Segurança Trânsito Visibilidade Máxima';
          default:
            return (
              categoryInfo.description + ' - ' + subCategoryInfo.description ||
              'RM Policarbonatos e Acessórios'
            );
        }
      }
    }
    return 'RM Policarbonatos e Acessórios';
  }
  getDescriptionPageSubCategory(category: string, subCategory: string) {
    const subUper = subCategory?.toUpperCase()?.replaceAll('-', '_');
    switch (subUper) {
      case SubCategoriesEnum.POLICARBONATO_ALVEOLAR:
        return 'Compre chapa de policarbonato alveolar cristal e transparente com qualidade garantida. Ideal para coberturas leves, resistentes e modernas!';
      case SubCategoriesEnum.TOLDO_EM_POLICARBONATO:
        return 'Adquira seu toldo em policarbonato alveolar fixo com instalação fácil. Durabilidade, leveza e proteção em diversos modelos e cores!';
      case SubCategoriesEnum.POLICARBONATO_COMPACTO_KIT:
        return 'Garanta sua chapa de policarbonato compacto 3mm em kit completo. Alta resistência, estética refinada e proteção UV para diversos projetos!';
      case SubCategoriesEnum.ROMANA:
        return 'Compre persiana romana blackout para quarto ou sala. Estilo, controle de luz e conforto térmico em modelos elegantes e sob medida!';
      case SubCategoriesEnum.DOUBLE_VISION:
        return 'Controle de luz e privacidade com persiana Double Vision blackout elegante e prática para ambientes sofisticados';
      case SubCategoriesEnum.POLICARBONATO_COMPACTO:
        return 'Chapa de policarbonato compacto de alta resistência e transparência ideal para coberturas e projetos duráveis';
      case SubCategoriesEnum.ROLO:
        return 'Peças e componentes para persianas que garantem funcionamento perfeito e acabamento profissional em qualquer ambiente';
      case SubCategoriesEnum.TOLDOS_CORTINA:
        return 'Toldo cortina enrolável ideal para proteção solar e conforto em áreas externas com design versátil e funcional';
      case SubCategoriesEnum.ACESSORIOS_PERSIANAS:
        return 'Persianas rolo e para quarto com qualidade e estilo. Compre online e parcele em até 4x semjuros';
      case SubCategoriesEnum.PERFIS_FIXACAO_ACABAMENTO:
        return 'Compre perfil U, H, trapézio e acabamento para policarbonato e pague em até 4x sem juros. Qualidade e entrega rápida.';
      case SubCategoriesEnum.POLICARBONATO_ALVEOLAR_KIT:
        return 'Kits de policarbonato alveolar para coberturas resistentes e modernas. Garanta praticidade, durabilidade e preço justo na RM Policarbonatos.';
      case SubCategoriesEnum.TOLDO_FIXO:
        return 'Toldo fixo em policarbonato sob medida para áreas externas. Proteção, design moderno e instalação de qualidade com a RM Policarbonatos.';
      case SubCategoriesEnum.HORIZONTAL:
        return 'Persiana horizontal de alumínio fabricada sob medida para sua janela com controle de luz, durabilidade e design elegante';
      case SubCategoriesEnum.PRONTA_ENTREGA:
        return 'Persianas em rolo, Double Vision e translúcidas com pronta entrega. Qualidade, estilo e funcionalidade em soluções práticas. | RM Policarbonatos';
      case SubCategoriesEnum.PLASTICOS_EMBALAGENS:
        return 'Peças e acessórios para embalagens plásticas direto da fábrica com bobina de plástico bolha ideal para proteção e transporte seguro';
      case SubCategoriesEnum.CANTONEIRAS_BARRA_CHATA:
        return 'Perfil de barra chata de alumínio natural para construção ou acabamento com cantoneira resistente e acabamento durável';
      case SubCategoriesEnum.PLACAS_SINALIZACAO:
        return 'Placas de sinalização para segurança e trânsito com alto padrão de visibilidade,resistentes para uso externo ou interno';
      default:
        return (
          'Descrição da Subcategoria: ' +
          this.getTitlePageSubCategory(category, subCategory)
        );
    }
  }

  getKeywordsPageSubCategory(subCategory: string) {
    const subUper = subCategory?.toUpperCase()?.replaceAll('-', '_');
    switch (subUper) {
      case SubCategoriesEnum.POLICARBONATO_ALVEOLAR:
        return 'chapa de policarbonato chapas de policarbonato chapa de policarbonato transparente chapa de policarbonato alveolar chapa de policarbonato alveolar cristal alveolar policarbonato policarbonato alveolar';
      case SubCategoriesEnum.TOLDO_EM_POLICARBONATO:
        return 'toldo em policarbonato toldos em policarbonato toldo em policarbonato alveolar toldo fixo em policarbonato';
      case SubCategoriesEnum.POLICARBONATO_COMPACTO_KIT:
        return 'chapa policarbonato compacta policarbonato compacto 3mm policarbonato compacto cristal policarbonato compacto fumê';
      case SubCategoriesEnum.ROMANA:
        return 'persiana romana blackout persiana romana cortina persiana romana persiana romana quarto';
      case SubCategoriesEnum.DOUBLE_VISION:
        return 'persiana blackout persiana double vision double vision persiana persiana rolo double vision';
      case SubCategoriesEnum.POLICARBONATO_COMPACTO:
        return 'policarbonato compacto chapas de policarbonato compacta chapa policarbonato compacto policarbonato compacto fume policarbonato cristal';
      case SubCategoriesEnum.ROLO:
        return 'persiana cortina persiana persianas persiana rolo persiana em quarto';
      case SubCategoriesEnum.TOLDOS_CORTINA:
        return 'toldo cortina toldos cortina toldo cortina de enrolar toldo cortina lona toldo cortina tela solar';
      case SubCategoriesEnum.ACESSORIOS_PERSIANAS:
        return 'persiana cortina persiana persianas persiana rolo persiana em quarto';
      case SubCategoriesEnum.PERFIS_FIXACAO_ACABAMENTO:
        return 'perfis em alumínio perfil de aluminio perfil de aluminio em u perfil u perfil trapézio perfil h perfil de acabamento perfil para policarbonato';
      case SubCategoriesEnum.POLICARBONATO_ALVEOLAR_KIT:
        return 'policarbonato alveolar alveolar policarbonato cobertura de policarbonato alveolar kit policarbonato alveolar';
      case SubCategoriesEnum.TOLDO_FIXO:
        return 'toldo fixo toldo fixo policarbonato toldo fixo de policarbonato toldo fixo em policarbonato';
      case SubCategoriesEnum.HORIZONTAL:
        return 'persiana horizontal persiana horizontal aluminio cortina persiana horizonta persiana sob medida';
      case SubCategoriesEnum.PRONTA_ENTREGA:
        return 'Persianas em rolo, Double Vision e translúcidas com pronta entrega. Qualidade, estilo e funcionalidade em soluções práticas. | RM Policarbonatos';
      case SubCategoriesEnum.PLASTICOS_EMBALAGENS:
        return 'embalagens de plástico fábrica de embalagens plásticas bobina plastico bolha bobina de plastico bolha';
      case SubCategoriesEnum.CANTONEIRAS_BARRA_CHATA:
        return 'barra chata aluminio natural barra chata de aluminio barra chata aluminio em barras chatas cantoneira de aluminio';
      case SubCategoriesEnum.PLACAS_SINALIZACAO:
        return 'placas de sinalização placa de sinalização placas de sinalização de segurança placas de sinalização de transito';
      default:
        return MetaTagsService.KEYWORDS_DEFAULT;
    }
  }

  classToBody(classes: string, add: boolean) {
    if (add) {
      this.renderer.addClass(this.dom.body, classes);
    } else {
      this.renderer.removeClass(this.dom.body, classes);
    }
  }

  getCategories() {
    if (this._categories?.length > 0) {
      return this._categories;
    } else {
      this._categories = this.appInfoService.getCategoriesWithProductActive();
      return this._categories;
    }
  }
}
